import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ObservableHelper } from 'src/app/helpers/observable-helper';
import { ImmoIPImmoTaskSubmitService } from 'src/app/services/immo-ip-immo-task-submit/immo-ip-immo-task-submit.service';
import { ImmoIPImmoTaskChangesObject } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-immo-task-changes/immo-ip-immo-task-changes-object';
import { ImmoIPImmoTaskChangesService } from 'src/app/services/immo-ip-placer-offline-changes/immo-ip-immo-task-changes/immo-ip-immo-task-changes.service';
import { ToDoItem } from 'src/app/services/immo-ip-placer-offline-changes/model/to-do-item';
import { SnackService } from 'src/app/services/snack.service';
import {
  ImmoTaskToDoItemProp,
  SetImmoTaskChangeFunction,
} from './immo-ip-placer-immo-task-change-functions';

export class ImmoIPPlacerImmoTaskChangeStorageHandler {
  private static readonly STORE_OFFLINE_CHANGE_DELAY = 1000;
  private storingOfflineChange = new BehaviorSubject<boolean>(false);
  private offlineImmoTaskStoreTriggerSubject =
    new Subject<ImmoIPImmoTaskChangesObject>();
  private storeOfflineChangeSubscription: Subscription;

  constructor(
    private dateString: string,
    private immoIPTaskChangesObject: ImmoIPImmoTaskChangesObject,
    private immoIPImmoTaskChangesService: ImmoIPImmoTaskChangesService,
    private immoIPImmoTaskSubmitService: ImmoIPImmoTaskSubmitService,
    private snackService: SnackService
  ) {
    this.storeOfflineChangeSubscription =
      this.offlineImmoTaskStoreTriggerSubject
        .pipe(
          debounceTime(
            ImmoIPPlacerImmoTaskChangeStorageHandler.STORE_OFFLINE_CHANGE_DELAY
          )
        )
        .subscribe({
          next: (immoTaskChanges) =>
            this.doStoreImmoTaskChanges(immoTaskChanges),
        });
  }

  public destroy(): void {
    if (this.storeOfflineChangeSubscription != null) {
      this.storeOfflineChangeSubscription.unsubscribe();
      this.storeOfflineChangeSubscription = null;
    }
    if (this.storingOfflineChange != null) {
      this.storingOfflineChange.complete();
      this.storingOfflineChange = null;
    }
    if (this.offlineImmoTaskStoreTriggerSubject != null) {
      this.offlineImmoTaskStoreTriggerSubject.complete();
      this.offlineImmoTaskStoreTriggerSubject = null;
    }
  }

  public fillImmoTaskToDoItem(
    toDoItem: ToDoItem,
    prop: ImmoTaskToDoItemProp
  ): void {
    if (this.immoIPTaskChangesObject[prop] != null) {
      toDoItem.done = this.immoIPTaskChangesObject[prop].done;
    }
  }

  public storeOfflineTaskChange<I>(
    arg: I,
    changeFunction: SetImmoTaskChangeFunction<I>
  ): void {
    changeFunction(this.immoIPTaskChangesObject, arg);
    this.storingOfflineChange.next(true);
    this.offlineImmoTaskStoreTriggerSubject.next(this.immoIPTaskChangesObject);
  }

  private doStoreImmoTaskChanges(
    immoTaskChanges: ImmoIPImmoTaskChangesObject
  ): void {
    this.storingOfflineChange.next(true);
    this.immoIPImmoTaskChangesService
      .storeObject(immoTaskChanges)
      .catch((err) => {
        if (err instanceof Error) {
          this.snackService.showError(err.message);
        } else {
          this.snackService.showError(err);
        }
      })
      .finally(() => this.storingOfflineChange.next(false));
  }

  public storeToDoItem(toDoItem: ToDoItem, prop: ImmoTaskToDoItemProp): void {
    this.immoIPTaskChangesObject[prop] = toDoItem;
    this.storingOfflineChange.next(true);
    this.offlineImmoTaskStoreTriggerSubject.next(this.immoIPTaskChangesObject);
  }

  public async submitImmoTask(): Promise<void> {
    await ObservableHelper.untilFirstSatisfying(
      this.storingOfflineChange,
      (e) => e === false
    ).toPromise();
    await this.immoIPImmoTaskSubmitService.submitImmoTask(
      this.immoIPTaskChangesObject,
      this.dateString
    );
    this.snackService.showSuccess(
      `Taak succesvol op ${
        this.immoIPTaskChangesObject.executed ? '' : ' niet '
      } uitgevoerd gezet`
    );
  }

  public async submitImmoTaskChanges(): Promise<void> {
    await ObservableHelper.untilFirstSatisfying(
      this.storingOfflineChange,
      (e) => e === false
    ).toPromise();
    await this.immoIPImmoTaskSubmitService.submitImmoTaskChanges(
      this.immoIPTaskChangesObject,
      this.dateString
    );
    this.snackService.showSuccess('Feedback succesvol aangepast');
  }

  public async submitResetNotExecuted(): Promise<void> {
    await ObservableHelper.untilFirstSatisfying(
      this.storingOfflineChange,
      (e) => e === false
    ).toPromise();
    await this.immoIPImmoTaskSubmitService.resetNotExecuted(
      this.immoIPTaskChangesObject,
      this.dateString
    );
    this.snackService.showSuccess('Niet uitgevoerd succesvol gereset');
  }

  public async submitImmoTaskAssets(): Promise<void> {
    await ObservableHelper.untilFirstSatisfying(
      this.storingOfflineChange,
      (e) => e === false
    ).toPromise();
    await this.immoIPImmoTaskSubmitService.directlySubmitOfflineAssets(
      this.immoIPTaskChangesObject,
      this.dateString
    );
    this.snackService.showSuccess('Foto succesvol toegevoegd');
  }

  public async submitDeleteAssets(): Promise<void> {
    await ObservableHelper.untilFirstSatisfying(
      this.storingOfflineChange,
      (e) => e === false
    ).toPromise();
    await this.immoIPImmoTaskSubmitService.deleteAssets(
      this.immoIPTaskChangesObject,
      this.dateString
    );
    this.snackService.showSuccess('Foto succesvol verwijderd');
  }

  public get currentImmoIPTaskChangesObject(): ImmoIPImmoTaskChangesObject {
    return this.immoIPTaskChangesObject;
  }

  public set currentImmoIPTaskChangesObject(
    immoIPTaskChangesObject: ImmoIPImmoTaskChangesObject
  ) {
    this.immoIPTaskChangesObject = immoIPTaskChangesObject;
  }

  public get isStoringOfflineChange(): boolean {
    return this.storingOfflineChange.value;
  }
}
